// src/pages/PricingPage.jsx
import React, { useContext, useState } from 'react';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { ThemeContext } from '../context/ThemeContext';
import { Link } from 'react-router-dom';

const PricingPage = () => {
  const { theme } = useContext(ThemeContext);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [plansState, setPlansState] = useState(null);
  const [savingsPercent, setSavingsPercent] = useState(null);

  const basePlans = [
    {
      name: 'Free',
      price: { monthly: '$0' },
      description: 'Perfect for getting started',
      features: [
        'Up to 50 notes',
        '5 AI searches per month',
        'Basic flashcard generation',
        'Standard search',
        'Ad-supported experience',
        'Community support',
      ],
      limitations: ['Limited AI features', 'No Zoom integration', 'With ads'],
      cta: 'Get Started Free',
      popular: false,
    },
    {
      name: 'Pro',
      price: { monthly: 'KSH 1,100',  },
      description: 'Most popular for power users',
      features: [
        'Unlimited notes',
        'Unlimited AI searches',
        'Advanced flashcard generation',
        'Advanced search with filters',
        'No ads',
        'Zoom meeting integration',
        'Priority support',
        'Export to multiple formats',
        'Custom templates',
        'Team collaboration (up to 3 users)',
      ],
      limitations: [],
      cta: 'Upgrade to Pro',
      popular: true,
    },
    {
      name: 'Team',
      price: { monthly: '$24.99' },
      description: 'For teams and organizations',
      features: [
        'Everything in Pro, plus:',
        'Up to 10 team members',
        'Shared workspaces',
        'Team analytics',
        'Admin controls',
        'SAML SSO',
        'Custom branding',
        'Dedicated account manager',
        'API access',
        'Training sessions',
      ],
      limitations: [],
      cta: 'Contact Sales',
      popular: false,
    },
  ];

  React.useEffect(() => {
    // Lazy-load pricing from backend and map into base plans
    (async () => {
      try {
        const paymentService = (await import('../services/paymentService')).default;
        const res = await paymentService.getPlans();
        if (res.status === 'success' && Array.isArray(res.data)) {
          const priceMap = {};
          res.data.forEach(p => {
            priceMap[`${p.plan}_${p.interval}`] = p;
          });

          const updated = basePlans.map(bp => {
            if (bp.name.toLowerCase() === 'free') return bp;
            const key = bp.name.toLowerCase();
            const monthly = priceMap[`${key}_monthly`]?.formatted || bp.price.monthly;
            const yearly = priceMap[`${key}_yearly`]?.formatted || bp.price.yearly;
            return { ...bp, price: { monthly, yearly }, _backendMonthly: priceMap[`${key}_monthly`], _backendYearly: priceMap[`${key}_yearly`] };
          });

          // Compute global savings percent for Pro yearly vs monthly if available
          const proMonthly = res.data.find(p => p.plan === 'pro' && p.interval === 'monthly');
          const proYearly = res.data.find(p => p.plan === 'pro' && p.interval === 'yearly');
          if (proMonthly && proYearly && proYearly.percentSaved) {
            setSavingsPercent(proYearly.percentSaved);
          }

          setPlansState(updated);
        } else {
          setPlansState(basePlans);
        }
      } catch (err) {
        setPlansState(basePlans);
      }
    })();
  }, []);

  return (
    <div className={`pricing-page ${theme === 'dark' ? 'dark' : ''}`}>
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-16">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              Choose Your Perfect Plan
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8">
              Whether you're a student, professional, or team, we have a plan that fits your needs
            </p>
            
            {/* Billing Toggle */}
            <div className="inline-flex items-center bg-gray-200 dark:bg-gray-700 rounded-lg p-1 mb-8">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-md font-medium transition ${
                  billingCycle === 'monthly'
                    ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                Monthly
              </button>
              
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {(plansState || basePlans).map((plan, index) => (
              <div
                key={index}
                className={`relative rounded-2xl border-2 p-8 transition-all hover:scale-[1.02] ${
                  plan.popular
                    ? 'border-blue-500 dark:border-blue-400 shadow-2xl'
                    : 'border-gray-200 dark:border-gray-700'
                } bg-white dark:bg-gray-800`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-bold">
                      MOST POPULAR
                    </span>
                  </div>
                )}

                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">{plan.description}</p>
                  
                  <div className="flex items-baseline mb-2">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">
                      {plan.price[billingCycle]}
                    </span>
                    <span className="text-gray-600 dark:text-gray-400 ml-2">
                      {plan.name !== 'Free' && `/${billingCycle === 'monthly' ? 'month' : 'year'}`}
                    </span>
                  </div>
                  {plan.name !== 'Free' && billingCycle === 'yearly' && (
                    <p className="text-sm text-green-600 dark:text-green-400">
                      Billed annually, cancel anytime
                    </p>
                  )}
                </div>

                <div className="mb-8">
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center">
                        <span className="text-green-500 mr-3">✓</span>
                        <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {plan.limitations.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Limitations:</p>
                      <ul className="space-y-2">
                        {plan.limitations.map((limitation, idx) => (
                          <li key={idx} className="flex items-center text-sm">
                            <span className="text-gray-400 mr-2">•</span>
                            <span className="text-gray-600 dark:text-gray-400">{limitation}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <Link
                  to={plan.name === 'Free' ? '/signup' : '/checkout'}
                  className={`block w-full text-center py-4 rounded-lg font-bold transition ${
                    plan.popular
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 text-white'
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>

          {/* FAQ Section */}
          <div className="max-w-4xl mx-auto mt-24">
            <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12">
              Frequently Asked Questions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  q: 'Can I switch between Free and Pro plans?',
                  a: 'Yes, you can upgrade or downgrade at any time. When upgrading, you\'ll get immediate access to Pro features. When downgrading, it takes effect at the end of your billing cycle.',
                },
                {
                  q: 'What happens to my data if I cancel?',
                  a: 'Your data is preserved for 30 days after cancellation. You can resubscribe within this period to restore full access. After 30 days, data may be permanently deleted.',
                },
                {
                  q: 'How does the AI search limit work?',
                  a: 'Free users get 5 AI searches per calendar month. The counter resets on the 1st of each month. Pro users have unlimited AI searches.',
                },
                {
                  q: 'What payment methods do you accept?',
                  a: 'We accept all major credit cards (Visa, MasterCard, American Express), PayPal, and bank transfers for annual plans.',
                },
                {
                  q: 'Is there a free trial for Pro features?',
                  a: 'Yes, we offer a 14-day free trial for the Pro plan. No credit card required to start the trial.',
                },
                {
                  q: 'Can I use IntelliNote for team collaboration?',
                  a: 'Yes, the Team plan includes shared workspaces, team management tools, and collaboration features for up to 10 users.',
                },
              ].map((faq, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-3">{faq.q}</h3>
                  <p className="text-gray-600 dark:text-gray-300">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center mt-20">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl p-12 text-white">
              <h2 className="text-3xl font-bold mb-4">Ready to supercharge your note-taking?</h2>
              <p className="text-xl mb-8 opacity-90">
                Join thousands of students and professionals who use IntelliNote AI
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/signup"
                  className="px-8 py-4 bg-white text-blue-600 font-bold rounded-lg hover:bg-gray-100 transition"
                >
                  Start Free Trial
                </Link>
                <Link
                  to="/contact"
                  className="px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-lg hover:bg-white/10 transition"
                >
                  Contact Sales
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PricingPage;